import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';

import {
  Drawer,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import CloseIcon from 'assets/img/picto-close.svg';
import { MOBILE } from 'settings/mediaQuery';
import { StockVariante } from 'types/StocksReference';

import styles from './SizeField.module.scss';

function SizeField({
  disableField,
  isValidReference,
  mediaMobile = false,
  reference,
  setQuantity,
  setSelectedVariant,
  stocksVariante,
}: {
  disableField: boolean;
  isValidReference: boolean;
  mediaMobile?: boolean;
  reference?: string;
  setQuantity: (qty: string) => void;
  setSelectedVariant: (selectedProduct: StockVariante | undefined) => void;
  stocksVariante?: StockVariante[];
}): JSX.Element {
  const [displayFullLabel, setDisplayFullLabel] = useState(false);
  const [showSelectSize, setShowSelectSize] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [size, setSize] = useState<string>('');

  const isMobile = useMediaQuery({
    query: MOBILE,
  });
  const { t } = useTranslation();

  useEffect(() => {
    if (stocksVariante) {
      setSize(
        stocksVariante?.find(product => parseInt(product.QUANTITE_STOCK) > 0)
          ?.TAILLE || ''
      );
      setQuantity('1');
    }
  }, [stocksVariante]);

  useEffect(() => {
    if (size) {
      const selectedProduct = stocksVariante?.find(
        product => product.TAILLE === size
      );
      setSelectedVariant(selectedProduct);
    }
  }, [size, stocksVariante]);

  const stockItems =
    stocksVariante && stocksVariante.length
      ? stocksVariante?.map(refStock => ({
          size: refStock.TAILLE,
          stockStatus: refStock.ETAT_STOCK,
          qty: refStock.QUANTITE_STOCK,
        }))
      : [];

  function isStockAvailable(stockStatus: string): boolean {
    return stockStatus === t('AVAILABLE');
  }

  return (
    <>
      <div
        className={classnames(styles.cell, {
          [styles.mediaMobile]: mediaMobile,
        })}
        id="field-size"
      >
        <FormControl
          className={styles.selectInput}
          focused={false}
          variant="outlined"
        >
          <Select
            classes={{
              root: styles.selectRoot,
            }}
            displayEmpty
            disabled={!reference || !isValidReference || disableField}
            IconComponent={ExpandMoreIcon}
            id="selectSize"
            input={
              <OutlinedInput
                name="size"
                id="outlinedSize"
                className={
                  !reference || !isValidReference || disableField
                    ? styles.disabledInput
                    : ''
                }
                classes={{
                  root: styles.outlinedRoot,
                  notchedOutline: styles.notchedOutline,
                  disabled: styles.disabled,
                }}
              />
            }
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              classes: { paper: styles.paper },
              MenuListProps: { classes: { root: styles.menu } },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            onChange={ev => {
              setSize(ev.target.value as string);
            }}
            onOpen={() => {
              setDisplayFullLabel(true);
              setShowSelectSize(true);
              if (isMobile) setShowDrawer(true);
            }}
            onClose={() => {
              setShowSelectSize(false);
              setDisplayFullLabel(false);
            }}
            value={size}
            open={!isMobile && showSelectSize}
          >
            <MenuItem
              className={styles.empty}
              classes={{ root: styles.stockItem }}
              value=""
            >
              {t('LABEL_SIZE')}
            </MenuItem>
            {stockItems.map(stockItem => {
              const isAvailable = isStockAvailable(stockItem.stockStatus);
              return (
                <MenuItem
                  className={!isAvailable ? styles.disabled : ''}
                  classes={{
                    root: classnames(styles.stockItem, {
                      [styles.disabled]: !isAvailable,
                    }),
                  }}
                  key={stockItem.size}
                  value={stockItem.size}
                >
                  {stockItem.size}
                  {displayFullLabel && `- ${stockItem.stockStatus}`}
                  <span
                    className={classnames({
                      [styles.available]:
                        isAvailable && parseInt(stockItem.qty) > 0,
                      [styles.out]: isAvailable && parseInt(stockItem.qty) == 0,
                      [styles.unavailable]: !isAvailable,
                    })}
                  />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
      <Drawer
        anchor="left"
        classes={{
          modal: styles.modalDrawer,
          paper: styles.paperDrawer,
          paperAnchorLeft: styles.paperAnchorLeft,
        }}
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
      >
        <div className={styles.header}>
          14
          <img
            src={CloseIcon}
            alt={t('LABEL_CLOSE')}
            className={styles.icon}
            onClick={() => setShowDrawer(false)}
          />
        </div>
        <div>
          {stockItems.map(stockItem => {
            const isAvailable = isStockAvailable(stockItem.stockStatus);
            return (
              <MenuItem
                className={!isAvailable ? styles.disabled : ''}
                classes={{
                  root: classnames(styles.stockItem, {
                    [styles.disabled]: !isAvailable,
                  }),
                }}
                key={stockItem.size}
                value={stockItem.size}
                onClick={() => {
                  setSize(stockItem.size);
                  setQuantity('1');
                  setShowDrawer(false);
                  setDisplayFullLabel(false);
                }}
              >
                <div>
                  {stockItem.size}
                  {displayFullLabel && ` - ${stockItem.stockStatus}`}
                  <span
                    className={classnames({
                      [styles.available]:
                        isAvailable && parseInt(stockItem.qty) > 0,
                      [styles.out]: isAvailable && parseInt(stockItem.qty) == 0,
                      [styles.unavailable]: !isAvailable,
                    })}
                  />
                </div>
                {isAvailable && <ExpandMoreIcon className={styles.expand} />}
              </MenuItem>
            );
          })}
        </div>
        <div>
          <button
            className={styles.backButton}
            type="button"
            onClick={() => setShowDrawer(false)}
          >
            <ArrowBackIosIcon className={styles.backArrow} />
            {t('LABEL_BACK')}
          </button>
        </div>
      </Drawer>
    </>
  );
}

export default SizeField;
